import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { BookOpen, Clock, ChevronRight, GraduationCap, Target, Award, Star, Lock } from "lucide-react";
import { API } from "../App";

const LearningHub = () => {
  const [tutorials, setTutorials] = useState({ beginner: [], intermediate: [], advanced: [] });
  const [activeTab, setActiveTab] = useState("beginner");
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const response = await axios.get(`${API}/tutorials`);
        setTutorials(response.data);
      } catch (error) {
        console.error("Error fetching tutorials:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorials();
  }, []);
  
  const levelInfo = {
    beginner: {
      icon: <GraduationCap size={24} />,
      title: "Beginner",
      description: "Start from zero and learn the fundamentals of trading",
      color: "var(--success)"
    },
    intermediate: {
      icon: <Target size={24} />,
      title: "Intermediate",
      description: "Build on basics with technical and fundamental analysis",
      color: "var(--warning)"
    },
    advanced: {
      icon: <Award size={24} />,
      title: "Advanced",
      description: "Master portfolio management and trading psychology",
      color: "var(--secondary)"
    }
  };
  
  return (
    <div className="learning-hub" data-testid="learning-hub">
      <div className="container">
        {/* Header */}
        <div className="hub-header" data-testid="hub-header">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1>Learning Hub</h1>
            <p>Master trading with our comprehensive curriculum</p>
          </motion.div>
        </div>
        
        {/* Level Selector */}
        <div className="level-selector" data-testid="level-selector">
          {Object.entries(levelInfo).map(([key, info]) => (
            <button
              key={key}
              className={`level-card ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
              data-testid={`level-${key}-btn`}
            >
              <div className="level-icon" style={{ backgroundColor: `${info.color}20`, color: info.color }}>
                {info.icon}
              </div>
              <div className="level-info">
                <h3>{info.title}</h3>
                <p>{info.description}</p>
              </div>
              <span className="level-count">{tutorials[key]?.length || 0} lessons</span>
            </button>
          ))}
        </div>
        
        {/* Tutorial List */}
        <div className="tutorials-section" data-testid="tutorials-section">
          <div className="tutorials-header">
            <span className={`level-badge ${activeTab}`}>{levelInfo[activeTab].title}</span>
            <h2>{tutorials[activeTab]?.length || 0} Tutorials Available</h2>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading tutorials...</p>
            </div>
          ) : (
            <div className="tutorials-grid">
              {tutorials[activeTab]?.map((tutorial, index) => (
                <motion.div
                  key={tutorial.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {tutorial.locked ? (
                    <Link 
                      to="/pricing"
                      className="tutorial-card card card-interactive locked"
                      data-testid={`tutorial-card-${tutorial.id}`}
                    >
                      <div className="tutorial-number">{String(index + 1).padStart(2, '0')}</div>
                      <div className="tutorial-content">
                        <h3>{tutorial.title}</h3>
                        <p>{tutorial.description}</p>
                        <div className="tutorial-meta">
                          <span className="duration">
                            <Clock size={14} />
                            {tutorial.duration}
                          </span>
                          <span className="locked-badge"><Lock size={12} /> Upgrade to unlock</span>
                        </div>
                      </div>
                      <div className="tutorial-action locked-icon">
                        <Lock size={20} />
                      </div>
                    </Link>
                  ) : (
                    <Link 
                      to={`/learn/${activeTab}/${tutorial.id}`}
                      className="tutorial-card card card-interactive"
                      data-testid={`tutorial-card-${tutorial.id}`}
                    >
                      <div className="tutorial-number">{String(index + 1).padStart(2, '0')}</div>
                      <div className="tutorial-content">
                        <h3>{tutorial.title}</h3>
                        <p>{tutorial.description}</p>
                        <div className="tutorial-meta">
                          <span className="duration">
                            <Clock size={14} />
                            {tutorial.duration}
                          </span>
                        </div>
                      </div>
                      <div className="tutorial-action">
                      <ChevronRight size={20} />
                    </div>
                  </Link>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
        
        {/* Learning Path */}
        <div className="learning-path-section" data-testid="learning-path-section">
          <h2>Recommended Learning Path</h2>
          <div className="path-steps">
            <div className="path-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Learn the Basics</h4>
                <p>Understand what trading is and key terminology</p>
              </div>
            </div>
            <div className="path-connector" />
            <div className="path-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Practice Paper Trading</h4>
                <p>Apply knowledge with virtual money</p>
              </div>
            </div>
            <div className="path-connector" />
            <div className="path-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Learn Analysis</h4>
                <p>Master technical and fundamental analysis</p>
              </div>
            </div>
            <div className="path-connector" />
            <div className="path-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Develop Your Strategy</h4>
                <p>Create and refine your trading approach</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .learning-hub {
          padding: 40px 0 80px;
        }
        
        .hub-header {
          text-align: center;
          margin-bottom: 48px;
        }
        
        .hub-header h1 {
          font-size: 3rem;
          margin-bottom: 12px;
        }
        
        .hub-header p {
          color: var(--text-secondary);
          font-size: 1.125rem;
        }
        
        /* Level Selector */
        .level-selector {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 48px;
        }
        
        .level-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          cursor: pointer;
          transition: border-color 0.2s, transform 0.2s;
          text-align: left;
        }
        
        .level-card:hover {
          border-color: var(--border-active);
        }
        
        .level-card.active {
          border-color: var(--primary);
          background: rgba(59, 130, 246, 0.05);
        }
        
        .level-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          flex-shrink: 0;
        }
        
        .level-info {
          flex: 1;
          min-width: 0;
        }
        
        .level-info h3 {
          font-size: 1rem;
          margin-bottom: 4px;
          color: var(--text-primary);
        }
        
        .level-info p {
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.4;
        }
        
        .level-count {
          font-size: 12px;
          color: var(--text-muted);
          white-space: nowrap;
        }
        
        /* Tutorials Section */
        .tutorials-section {
          margin-bottom: 64px;
        }
        
        .tutorials-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .tutorials-header h2 {
          font-size: 1.25rem;
          font-weight: 500;
        }
        
        .tutorials-grid {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .tutorial-card {
          display: flex;
          align-items: center;
          gap: 24px;
          text-decoration: none;
          color: inherit;
        }
        
        .tutorial-number {
          font-family: var(--font-mono);
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--text-muted);
          width: 48px;
          flex-shrink: 0;
        }
        
        .tutorial-content {
          flex: 1;
        }
        
        .tutorial-content h3 {
          font-size: 1.125rem;
          margin-bottom: 4px;
        }
        
        .tutorial-content p {
          color: var(--text-secondary);
          font-size: 14px;
        }
        
        .tutorial-meta {
          display: flex;
          gap: 16px;
          margin-top: 8px;
        }
        
        .duration {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: var(--text-muted);
        }
        
        .tutorial-action {
          color: var(--text-muted);
          transition: color 0.2s, transform 0.2s;
        }
        
        .tutorial-card:hover .tutorial-action {
          color: var(--primary);
          transform: translateX(4px);
        }

        .tutorial-card.locked {
          opacity: 0.7;
          border-style: dashed;
        }
        .tutorial-card.locked:hover {
          border-color: var(--gold);
        }
        .tutorial-card.locked:hover .tutorial-action {
          color: var(--gold);
          transform: none;
        }
        .locked-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--gold);
          background: rgba(255,215,0,0.1);
          padding: 2px 8px;
          border-radius: 8px;
        }
        .locked-icon {
          color: var(--gold) !important;
        }
        
        /* Learning Path */
        .learning-path-section {
          background: var(--bg-paper);
          border-radius: 16px;
          padding: 40px;
        }
        
        .learning-path-section h2 {
          text-align: center;
          margin-bottom: 40px;
        }
        
        .path-steps {
          display: flex;
          align-items: flex-start;
          justify-content: center;
          gap: 16px;
        }
        
        .path-step {
          text-align: center;
          max-width: 180px;
        }
        
        .step-number {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 50%;
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0 auto 16px;
        }
        
        .step-content h4 {
          font-size: 1rem;
          margin-bottom: 4px;
        }
        
        .step-content p {
          font-size: 13px;
          color: var(--text-muted);
        }
        
        .path-connector {
          width: 60px;
          height: 2px;
          background: var(--border);
          margin-top: 20px;
        }
        
        /* Loading */
        .loading-state {
          text-align: center;
          padding: 60px;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 1024px) {
          .level-selector {
            grid-template-columns: 1fr;
          }
          
          .path-steps {
            flex-direction: column;
            align-items: center;
          }
          
          .path-connector {
            width: 2px;
            height: 40px;
            margin: 0;
          }
        }
        
        @media (max-width: 768px) {
          .tutorial-card {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .tutorial-number {
            width: auto;
          }
          
          .tutorial-action {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default LearningHub;